import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { map } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {
  useOrganizationRoleDelete,
  useOrganizationRoles,
  useOrganizationRolesAdd,
} from "../organizationServices";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import { formatRoleLabel } from "../../util/stringFormat";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import OrganizationRoleDialog from "../edit/OrganiationRoleDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationRolesCard = ({ id }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [roles, setRoles] = useState([]);

  const rolesResponse = useOrganizationRoles(id);
  useEffect(() => {
    if (rolesResponse.isSuccess) {
      setRoles(rolesResponse.data);
    }
  }, [rolesResponse.isSuccess, rolesResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleRolesCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleRolesSave = useCallback(
    async data => {
      await rolesResponse.refetch();
      setDialogOpen(false);
    },
    [rolesResponse],
  );

  const addOrgRoles = useOrganizationRolesAdd();
  const handleUndo = roleName => async () => {
    try {
      await addOrgRoles.mutateAsync({
        resourceIds: { orgId: id },
        data: [roleName],
      });
    } catch (err) {
      alertError(err);
    }
  };

  const roleDelete = useOrganizationRoleDelete();
  const handleDeleteClick = async role => {
    try {
      await roleDelete.mutateAsync({
        resourceIds: {
          orgId: id,
          role,
        },
      });
      await rolesResponse.refetch();
    } catch (err) {
      alertError(err);
    }
  };

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Roles"
        action={
          <Tooltip title="Add Roles">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {rolesResponse.isError && (
          <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
            Error loading Roles.
          </Box>
        )}
        {rolesResponse.isLoading && <PanelLoadingIndicator />}
        {rolesResponse.isSuccess && isBlank(roles) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Roles set up for this Organization.
          </Box>
        )}
        {rolesResponse.isSuccess && notBlank(roles) && (
          <List dense>
            {map(
              role => (
                <ListItem key={role.roleName}>
                  <ListItemText primary={formatRoleLabel(role.roleName)} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      data={role.roleName}
                      handleUndo={handleUndo}
                      handleDelete={() => handleDeleteClick(role.roleName)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              roles,
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <OrganizationRoleDialog
          organizationId={id}
          organizationRoles={roles}
          open={dialogOpen}
          onCancel={handleRolesCancel}
          onSave={handleRolesSave}
        />
      )}
    </Card>
  );
};

export default OrganizationRolesCard;
